import { Collapse } from "bootstrap"
import { Controller } from "@hotwired/stimulus"
import { isFocusable, isReachable } from "../../utils/field-visibility.util"

// Connects to data-controller="documents--mandatory-items"
//
// Turns the sidebar's "Povinné položky" list into a table of contents over the
// inline field errors: clicking an entry opens the accordion the field lives
// in, scrolls to it, focuses it and flashes it. The panel lives outside the
// <form>, so it also listens for the correction event `form--validation`
// dispatches on `document` — otherwise a field the user has already fixed
// would keep an entry here contradicting the (now cleared) inline hint.
export default class extends Controller {
  static targets = ["entry", "count"]

  static values = {
    correctedEvent: { type: String, default: "form--validation:fieldCorrected" },
    // A panel carrying the checklist stays when its last open entry goes:
    // the ticked boxes are still worth seeing, and the live refresh redraws it.
    persistent: { type: Boolean, default: false }
  }

  connect() {
    this.onFieldCorrected = this.#dismissEntriesFor.bind(this)
    document.addEventListener(this.correctedEventValue, this.onFieldCorrected)
  }

  disconnect() {
    document.removeEventListener(this.correctedEventValue, this.onFieldCorrected)
    this.#clearFlash()
  }

  focusField(event) {
    event.preventDefault()

    const { fieldId, anchorId, flash } = event.currentTarget.dataset
    const field = this.#visibleElement(fieldId)
    const anchor = this.#visibleElement(anchorId)
    const target = field || anchor
    if (!target) return

    this.#revealAncestors(target)

    const behavior = window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth"
    ;(anchor || target).scrollIntoView({ behavior, block: "center" })

    if (isFocusable(target) && typeof target.focus === "function") target.focus({ preventScroll: true })
    this.#flash(target, flash)
  }

  #dismissEntriesFor(event) {
    const fieldIds = event.detail?.fieldIds
    if (!Array.isArray(fieldIds) || fieldIds.length === 0) return

    const dismissed = this.entryTargets.filter((entry) => fieldIds.includes(entry.dataset.fieldId))
    if (dismissed.length === 0) return

    dismissed.forEach((entry) => {
      const list = entry.parentElement
      entry.remove()
      // An emptied error list would keep its margin above the checklist until
      // the live refresh redraws the panel.
      if (list && list.children.length === 0) list.remove()
    })
    this.#refreshCount()
  }

  #refreshCount() {
    const remaining = this.entryTargets.length

    if (this.hasCountTarget) {
      this.countTarget.textContent = String(remaining)
      this.countTarget.classList.toggle("d-none", remaining === 0)
    }
    // `this.element` wraps the panel AND its trailing separator, so hiding it
    // leaves no orphaned rule behind in the sidebar.
    if (remaining === 0 && !this.persistentValue) this.element.classList.add("d-none")
  }

  // An element is only a usable jump target when it is actually rendered:
  // the customer picker's <select> and the payment-shape item columns are
  // `d-none`, so landing on them would scroll nowhere.
  #visibleElement(id) {
    if (!id) return null
    const element = document.getElementById(id)
    return isReachable(element) ? element : null
  }

  // The items table and the extra-details block sit in collapsed accordions on
  // a fresh form. Open them through Bootstrap so the toggle's aria-expanded
  // stays in sync instead of only flipping the `.show` class.
  #revealAncestors(element) {
    let collapse = element.closest(".collapse:not(.show)")

    while (collapse) {
      Collapse.getOrCreateInstance(collapse, { toggle: false }).show()
      collapse = collapse.parentElement?.closest(".collapse:not(.show)")
    }
  }

  // An entry marked `data-flash="todo"` is a field that is only still empty,
  // not an error, so it flashes in the brand colour instead of red.
  #flash(element, kind) {
    this.#clearFlash()
    this.flashed = element
    element.classList.add(...flashClasses(kind))
    this.flashTimeout = window.setTimeout(() => this.#clearFlash(), 2000)
  }

  #clearFlash() {
    if (this.flashTimeout) window.clearTimeout(this.flashTimeout)
    this.flashTimeout = null
    if (this.flashed) this.flashed.classList.remove(...flashClasses("todo"))
    this.flashed = null
  }
}

function flashClasses(kind) {
  return kind === "todo"
    ? ["mandatory-items__target--flash", "mandatory-items__target--flash-todo"]
    : ["mandatory-items__target--flash"]
}
